import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, CAMPAIGN_STAGES as STAGES, nextStage, fullName } from "@/lib/api";

export default function ComposeDialog({ lead, open, onOpenChange, onSent, advance = true }) {
  const [templates, setTemplates] = useState([]);
  const [pages, setPages] = useState([]);
  const [stage, setStage] = useState("");
  const [tpl, setTpl] = useState("");
  const [lp, setLp] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [cta, setCta] = useState("Book your free consultation");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || !lead) return;
    const st = lead.stage === "not_reviewed" ? "cold_email" : advance && lead.last_contacted_at ? nextStage(lead.stage) : lead.stage;
    setStage(st); setPreview(null);
    const scope = { params: lead.campaign_id ? { campaign: lead.campaign_id, shared: true } : {} };
    Promise.all([api.get("/templates", scope), api.get("/landing-pages", scope), lead.campaign_id ? api.get(`/campaigns/${lead.campaign_id}`) : Promise.resolve({ data: null })]).then(([t, p, c]) => {
      const own = (x) => (x.campaign_id === lead.campaign_id ? 0 : 1);
      const tl = [...t.data].sort((a, b) => own(a) - own(b));
      setTemplates(tl); setPages(p.data);
      const step = c.data?.steps?.find((s) => s.stage === st && s.template_id);
      const def = tl.find((x) => x.id === step?.template_id) || tl.find((x) => x.stage === st) || tl[0];
      if (def) { setTpl(def.id); setSubject(def.subject); setBody(def.body); setCta(def.cta_text || "Book your free consultation"); }
      const lpDef = p.data.find((x) => x.id === (step?.landing_page_id || c.data?.landing_page_id)) || p.data.find((x) => x.campaign_id === lead.campaign_id) || p.data[0];
      if (lpDef) setLp(lpDef.id);
    });
  }, [open, lead, advance]);

  const pickTpl = (id) => { const t = templates.find((x) => x.id === id); setTpl(id); setSubject(t.subject); setBody(t.body); setCta(t.cta_text || "Book your free consultation"); setPreview(null); };
  const aiDraft = async () => {
    setBusy("ai");
    try { const { data } = await api.post(`/leads/${lead.id}/ai-draft`, { stage }); setSubject(data.subject || ""); setBody(data.body || ""); setTpl(""); setPreview(null); toast.success("AI draft ready"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const doPreview = async () => {
    const { data } = await api.post(`/leads/${lead.id}/preview`, { subject, body, landing_page_id: lp || null, cta_text: cta });
    setPreview(data);
  };
  const send = async () => {
    setBusy("send");
    try {
      await api.post(`/leads/${lead.id}/send`, { subject, body, template_id: tpl || null, landing_page_id: lp || null, stage, cta_text: cta });
      toast.success("Added to Outbox. Review and send it from the Outbox.");
      window.dispatchEvent(new Event("outbox-changed"));
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="compose-dialog">
        <DialogHeader><DialogTitle>Email {fullName(lead)} · {lead?.company_name}</DialogTitle></DialogHeader>
        {(lead?.outcome === "unsubscribed" || lead?.email_status === "bounced") && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm p-3" data-testid="compose-blocked-warning">
            {lead.outcome === "unsubscribed" ? "This lead has unsubscribed — emails cannot be sent." : "This email address hard-bounced — update the lead's email before sending."}
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-3">
          <div><div className="label-xs mb-1">Move to stage</div>
            <Select value={stage} onValueChange={setStage}><SelectTrigger data-testid="compose-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.n}. {s.label}</SelectItem>)}</SelectContent></Select></div>
          <div><div className="label-xs mb-1">Template</div>
            <Select value={tpl} onValueChange={pickTpl}><SelectTrigger data-testid="compose-template-select"><SelectValue placeholder="Custom" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          <div><div className="label-xs mb-1">Landing page</div>
            <Select value={lp} onValueChange={setLp}><SelectTrigger data-testid="compose-landing-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{pages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <Input data-testid="compose-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
        <Textarea data-testid="compose-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="font-mono text-[13px]" />
        <div><div className="label-xs mb-1">Button text</div><Input value={cta} onChange={(e) => setCta(e.target.value)} data-testid="compose-cta-input" /></div>
        <p className="text-xs text-slate-500">Put {"{{landing_button}}"} on its own line to show the call-to-action button. Merge fields: {"{{first_name}} {{company_name}} {{landing_link}} {{tidycal_link}} {{sender_name}}"}. Links are tracked automatically.</p>
        {preview && (
          <div className="border rounded-lg p-4 bg-slate-50" data-testid="compose-preview">
            <div className="font-semibold mb-2">{preview.subject}</div>
            <div className="prose-email text-sm" dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        )}
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            <Button variant="outline" onClick={aiDraft} disabled={!!busy} data-testid="compose-ai-draft-btn" className="border-violet-300 text-violet-700">
              <Sparkles size={15} className="mr-1" />{busy === "ai" ? "Drafting…" : "AI draft"}</Button>
            <Button variant="outline" onClick={doPreview} data-testid="compose-preview-btn">Preview</Button>
          </div>
          <Button onClick={send} disabled={!!busy || !subject || lead?.outcome === "unsubscribed" || lead?.email_status === "bounced"} data-testid="compose-send-btn"><Send size={15} className="mr-1" />{busy === "send" ? "Adding…" : "Add to Outbox"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
